/**
 * The Rendering Engine - Recursive Block Renderer
 * Renders blocks recursively to handle nested structures (rows/columns)
 */

import React, { Suspense } from 'react';
import { getBlockComponent, type BlockComponentProps } from './BlockRegistry';
import { resolveTokens } from '@agentic/magento-client/lib/editor/TokenResolver';
import { salesFromContext } from '../../lib/product-sales';
import { getProductAssurance } from '../../lib/product-assurance';
import { BlockErrorBoundary } from './BlockErrorBoundary';

// TYPES
export interface BlockNode {
  id: string;
  type: string;
  content?: unknown;
  styles?: string;
  children?: BlockNode[];
  [key: string]: unknown;
}

export interface BlockRendererProps {
  /** The block JSON object from Admin */
  block: BlockNode;
  /** Optional context data (sku, categoryId, etc.) */
  context?: unknown;
}

// RECURSIVE BLOCK RENDERER
/**
 * Recursively renders a block and its children
 * 
 * Usage:
 * <BlockRenderer block={block} context={{ sku: "123" }} />
 */
export const BlockRenderer: React.FC<BlockRendererProps> = ({ block, context }) => {
  // Validate block
  if (!block || typeof block !== 'object') {
    console.error('[BlockRenderer] Invalid block:', block);
    return null;
  }

  if (!block.type) {
    console.error('[BlockRenderer] Block missing type:', block);
    return null;
  }

  const sales = salesFromContext(context);
  if (sales) {
    if (block.type === 'review_summary' && !(((context as {product?: {review_count?: number}})?.product?.review_count ?? 0) > 0)) return null;
    if (sales.profile.journey === 'capability' && block.type === 'cms_block' && (block.content as {identifier?: string})?.identifier === 'checkout_button_payment_strip') return null;
    if (['stock_status','stock_indicator','product_compatibility','product_setup'].includes(block.type)) return null;
    if (sales.profile.journey === 'capability' && ['product_price','qty_selector','tier_prices','tier_pricing','whats_in_the_box','configurable_options','product_options','payment_icons','trust_badges'].includes(block.type)) return null;
  }

  // Get the component for this block type
  const Component = getBlockComponent(block.type);

  // Recursively render children if they exist
  let renderedChildren: React.ReactNode = null;

  if (block.children && Array.isArray(block.children) && block.children.length > 0) {
    renderedChildren = (
      <>
        {block.children.map((childBlock, idx) => (
          <BlockRenderer
            // Stable key — index-based when childBlock.id is missing. Using
            // Math.random() here produced fresh keys on every render (server
            // AND client), so React saw a tree mismatch on hydration and
            // surfaced as the #418 minified text mismatch when a child
            // emitted any text. Index keys are deterministic across SSR+CSR.
            key={childBlock.id || `${block.id || 'b'}-child-${idx}`}
            block={childBlock}
            context={context}
          />
        ))}
      </>
    );
  }

  // : Dynamic Token Resolution
  // Resolve {{product.name}}, {{product.price_range?.minimum_price...}} etc.
  // inside block.content before passing it to the component. The resolver
  // walks nested objects/arrays recursively and never throws.
  const rawContent = block.content ?? {};
  const resolvedContent = resolveTokens(rawContent, context);
  // Gate before the lazy component, Suspense skeleton and leaf wrapper.
  if (block.type === 'product_assurance' && !getProductAssurance(resolvedContent, context)) return null;

  // : Variant extraction
  // The variant can be specified in:
  //   1. block.content.variant  (resolved by TokenResolver above)
  //   2. block.settings.variant (static JSON key)
  //   3. block.variant          (top-level override)
  interface BlockExtras { settings?: { variant?: string }; variant?: string; hoverStyles?: string }
  const blockExtras = block as unknown as BlockExtras;
  const resolvedVariant = (resolvedContent as { variant?: string } | undefined)?.variant;
  const variant: string | undefined =
    resolvedVariant ??
    blockExtras.settings?.variant ??
    blockExtras.variant;

  // Assemble component props.
  // CONTEXT PIPE VERIFIED: `context` (containing {sku, product, storeConfig, cmsBlocks})
  // is passed to every block component — including registry aliases:
  //   GridRow (row/col), ProductTitle (product_name), ProductGallery (product_images),
  //   ProductPrice, AddToCart, etc.
  // Commerce blocks consume context via useProduct(context) which reads
  // context.product as the SSR fast-path (no extra network call needed).
  const parsedClassName = typeof block.parsedClassName === 'string' ? block.parsedClassName : '';
  const parsedStyle = (block.parsedStyle && typeof block.parsedStyle === 'object')
    ? block.parsedStyle as Record<string, string>
    : {};
  const componentProps: BlockComponentProps & { hoverStyles?: string, blockId?: string } = {
    data: resolvedContent as BlockComponentProps['data'],
    styles: block.styles ?? '',
    className: parsedClassName,
    style: parsedStyle,
    context: context as BlockComponentProps['context'],
    children: renderedChildren,
    variant,
    hoverStyles: blockExtras.hoverStyles,
    blockId: block.id,
  };

  // Lightweight skeleton shown while lazy-loaded blocks are fetching
  const BlockSkeleton = () => (
    <div className="animate-pulse" style={{ minHeight: 60, background: 'rgba(255,255,255,0.03)', borderRadius: 12 }} />
  );

  // Structural blocks (row, column, section, container) manage their own root
  // element — an extra wrapper div breaks CSS grid by inserting an anonymous
  // div between the grid container and its direct grid-item children.
  const STRUCTURAL_TYPES = new Set(['row', 'grid_row', 'column', 'col', 'section', 'container', 'search_bar', 'search']);

  if (STRUCTURAL_TYPES.has(block.type)) {
    return (
      <BlockErrorBoundary blockType={block.type} blockId={block.id}>
        <Suspense fallback={<BlockSkeleton />}>
          <Component {...componentProps} data-block-id={block.id} data-block-type={block.type} />
        </Suspense>
      </BlockErrorBoundary>
    );
  }

  // Leaf blocks: wrapper div applies parsedClassName / parsedStyle.
  // Fallback id derived from block.type + position-stable signal (here:
  // block.id should always be present from the SSR layout fetcher; the
  // fallback is a safety net for legacy blocks). Use type + JSON content
  // hash collapse to a stable string — NOT Math.random which produced a
  // new value on every render and contributed to React #418 mismatches
  // when the hover-CSS selector embedded the random id in the DOM.
  const blockIdAttr = block.id || `leaf-${block.type}`;
  const hoverCss = blockExtras.hoverStyles ? blockExtras.hoverStyles.replace(/\{\{SELECTOR\}\}/g, `[data-block-id="${blockIdAttr}"]`) : null;

  return (
    <BlockErrorBoundary blockType={block.type} blockId={blockIdAttr}>
      <div className={`is-layout-container ${componentProps.className ?? ''}`.trim()} style={componentProps.style} data-block-type={block.type} data-block-id={blockIdAttr}>
        {hoverCss && <style dangerouslySetInnerHTML={{ __html: hoverCss }} />}
        <Suspense fallback={<BlockSkeleton />}>
          <Component {...componentProps} />
        </Suspense>
      </div>
    </BlockErrorBoundary>
  );
};

// MULTI-BLOCK RENDERER
/**
 * Renders an array of blocks (e.g., page sections)
 * 
 * Usage:
 * <BlocksRenderer blocks={pageBlocks} context={{ categoryId: "5" }} />
 */
export interface BlocksRendererProps {
  /** Array of block JSON objects */
  blocks: BlockNode[];
  /** Optional context data */
  context?: unknown;
  /** Optional wrapper className */
  className?: string;
}

export const BlocksRenderer: React.FC<BlocksRendererProps> = ({
  blocks,
  context,
  className
}) => {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return (
      <div className="p-8 text-center text-[var(--text-secondary)] border-2 border-dashed border-gray-200 rounded">
        <p>No blocks to render</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {blocks.map((block, idx) => (
        <BlockRenderer
          // Stable index-based key — see BlockRenderer key comment above.
          key={block.id || `block-${idx}`}
          block={block}
          context={context}
        />
      ))}
    </div>
  );
};

// DEBUG RENDERER (for development)
/**
 * Renders blocks with debug information
 */
export const DebugBlockRenderer: React.FC<BlockRendererProps & { depth?: number }> = ({
  block,
  context,
  depth = 0
}) => {
  const indent = '  '.repeat(depth);

  return (
    <div className="border border-gray-300 rounded overflow-hidden">
      <div className="bg-gray-100 px-4 py-2 text-sm font-mono border-b">
        {indent}🔹 {block.type} (id: {block.id})
      </div>
      <div className={depth > 0 ? 'ml-4' : ''}>
        <BlockRenderer block={block} context={context} />
      </div>
    </div>
  );
};

// EXPORTS
export default BlockRenderer;
